/**
 * Arvana lead events. Publishes custom events on Shopify's analytics bus so a Customer events
 * pixel (Settings → Customer events) can forward them to GA4. Nothing here talks to Google
 * directly, so consent stays with Shopify's customer privacy settings and the pixel.
 *
 * Events (each payload has `label`, `url` and `page`):
 * - arvana_cta_click: Arvana buttons and any #schedule-call link
 * - arvana_phone_click / arvana_email_click: tel: and mailto: links
 * - arvana_contact_submit: Contact page form, after Shopify confirms the message was sent
 * - arvana_call_request_open / arvana_call_request_submit: Schedule a Call dialog
 *   (from `arvana:schedule-call:*` events dispatched by assets/arvana-schedule-call.js)
 * - Blog and FAQ: links and buttons inside an element with `data-arvana-event="<name>"` publish
 *   that event with `data-arvana-label` as the label: arvana_blog_filter (category),
 *   arvana_featured_click and arvana_related_click (article title), arvana_toc_click (heading),
 *   arvana_share_click (network), arvana_product_card_click (product handle)
 * - Anything dispatched as `arvana:track` with `{ name, data }`: arvana_article_read
 *   (assets/arvana-article.js), arvana_faq_open, arvana_faq_helpful and arvana_faq_search
 *   (assets/arvana-help-center.js)
 */

const CTA_SELECTOR = '.arvana-button, a[href*="#schedule-call"]';

/**
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
function publish(name, data) {
  const payload = { page: window.location.pathname, ...data };
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    console.warn('[arvana-analytics]', name, error);
  }
}

/** @param {Element} element */
function labelOf(element) {
  const text = element.getAttribute('aria-label') || element.textContent || '';
  return text.replace(/\s+/g, ' ').trim().slice(0, 100);
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    // Blog and FAQ elements name their own event; they are not lead CTAs
    const tracked = event.target.closest('[data-arvana-event]');
    const control = event.target.closest('a[href], button');
    if (tracked instanceof HTMLElement && control && tracked.contains(control)) {
      const url = control instanceof HTMLAnchorElement ? control.href : window.location.href;
      publish(tracked.dataset.arvanaEvent ?? '', { label: tracked.dataset.arvanaLabel ?? labelOf(control), url });
      return;
    }

    const link = event.target.closest('a[href]');
    if (link instanceof HTMLAnchorElement) {
      const href = link.getAttribute('href') ?? '';
      if (href.startsWith('tel:')) {
        publish('arvana_phone_click', { label: labelOf(link), url: href });
        return;
      }
      if (href.startsWith('mailto:')) {
        publish('arvana_email_click', { label: labelOf(link), url: href });
        return;
      }
    }

    const cta = event.target.closest(CTA_SELECTOR);
    if (cta) {
      const url = cta instanceof HTMLAnchorElement ? cta.href : '';
      const section = cta.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') ?? '';
      publish('arvana_cta_click', { label: labelOf(cta), url, section });
    }
  },
  { capture: true }
);

document.addEventListener('arvana:track', (event) => {
  if (!(event instanceof CustomEvent) || typeof event.detail?.name !== 'string') return;
  publish(event.detail.name, { url: window.location.href, ...event.detail.data });
});

document.addEventListener('arvana:schedule-call:open', () => {
  publish('arvana_call_request_open', { label: 'Schedule a Call', url: window.location.href });
});

document.addEventListener('arvana:schedule-call:submitted', () => {
  publish('arvana_call_request_submit', { label: 'Schedule a Call', url: window.location.href });
});

// The Contact form posts and reloads with a success block; count each confirmation once
const contactSuccess = document.querySelector('.arvana-contact__success');
if (contactSuccess) {
  const key = `arvana-contact-sent:${window.location.href}`;
  let alreadySent = false;
  try {
    alreadySent = sessionStorage.getItem(key) === '1';
    sessionStorage.setItem(key, '1');
  } catch {
    // Storage blocked: publish anyway
  }
  if (!alreadySent) publish('arvana_contact_submit', { label: 'Contact form', url: window.location.href });
}
